import { client } from "../gqlClient";
import {
  convertAdminPgToGraphqlRelayPg,
  convertAdminSortToGraphqlSort,
} from "../utils";
import {
  CREATE_CLASS_STYLE,
  DELETE_CLASS_STYLE,
  GET_LIST_CLASS_STYLES,
  UPDATE_CLASS_STYLE,
} from "./schema";

// react-admin record id = slug (so the class-edit dropdown resolves on `style`);
// classStyleId/uuid = entity id (for mutations + publish/unpublish).
function toRecord(node) {
  return {
    id: node.slug,
    classStyleId: node.id,
    uuid: node.id,
    name: node.name,
    slug: node.slug,
    hidden: node.hidden,
    sortIndex: node.sortIndex,
    classCount: node.classCount,
  };
}

// `classCount` is a computed field (no DB column), so it can't be ordered
// server-side. The taxonomy is small, so for that column we fetch the full list
// and sort + paginate in-memory; every other field sorts server-side as usual.
export async function getListClassStyles(params) {
  if (params.sort && params.sort.field === "classCount") {
    return getListSortedByClassCount(params);
  }
  const data = await client.query(
    GET_LIST_CLASS_STYLES,
    {
      filter: params.filter,
      pagination: convertAdminPgToGraphqlRelayPg(params.pagination),
      sort: convertAdminSortToGraphqlSort(params.sort),
    },
    "classStyles"
  );
  return { data: data.edges.map((e) => toRecord(e.node)), total: data.totalCount };
}

async function getListSortedByClassCount(params) {
  const data = await client.query(
    GET_LIST_CLASS_STYLES,
    {
      filter: params.filter,
      pagination: convertAdminPgToGraphqlRelayPg({ page: 1, perPage: 1000 }),
      sort: { sortBy: "sortIndex", sortOrder: "asc" },
    },
    "classStyles"
  );
  const direction = params.sort.order === "DESC" ? -1 : 1;
  const sorted = data.edges
    .map((e) => toRecord(e.node))
    .sort((a, b) => (Number(a.classCount) - Number(b.classCount)) * direction);
  const { page, perPage } = params.pagination;
  const start = (page - 1) * perPage;
  return { data: sorted.slice(start, start + perPage), total: data.totalCount };
}

// Styles are a small fixed taxonomy; fetch the full list (valid sort + a high page
// limit) and resolve by slug. The list node already carries every field the edit
// form needs (incl. classStyleId/uuid for mutations), so no per-id query is needed.
const FULL_LIST_PARAMS = {
  filter: {},
  pagination: { page: 1, perPage: 1000 },
  sort: { field: "sortIndex", order: "ASC" },
};

export async function getOneClassStyle({ id }) {
  const { data } = await getListClassStyles(FULL_LIST_PARAMS);
  return { data: data.find((r) => r.id === id) || null };
}

export async function getManyClassStyles(params) {
  const { data } = await getListClassStyles(FULL_LIST_PARAMS);
  const wanted = new Set(params?.ids || []);
  return { data: data.filter((r) => wanted.has(r.id)) };
}

export async function classStyleCreate(params) {
  const res = await client.mutate(CREATE_CLASS_STYLE, { input: { name: params.data.name } });
  return { data: toRecord(res.adminClassStyleCreate) };
}

export async function classStyleUpdate(params) {
  const { data } = params;
  const res = await client.mutate(UPDATE_CLASS_STYLE, {
    input: { classStyleId: data.classStyleId, name: data.name, sortIndex: data.sortIndex },
  });
  return { data: toRecord(res.adminClassStyleUpdate) };
}

export async function classStylePublish(params) {
  const res = await client.mutate(UPDATE_CLASS_STYLE, { input: { classStyleId: params.uuid, hidden: false } });
  return { data: toRecord(res.adminClassStyleUpdate) };
}

export async function classStyleUnPublish(params) {
  const res = await client.mutate(UPDATE_CLASS_STYLE, { input: { classStyleId: params.uuid, hidden: true } });
  return { data: toRecord(res.adminClassStyleUpdate) };
}

// Hard delete; only allowed for styles with no linked classes (enforced server-side too).
// record id = slug, so use previousData.classStyleId (the entity uuid) for the mutation.
export async function classStyleDelete(params) {
  await client.mutate(DELETE_CLASS_STYLE, {
    classStyleId: params.previousData.classStyleId,
  });
  return { data: { id: params.id } };
}
